import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { getDeptCategory, getMainDept, getSubDept, getUserDesign, getUserPosit, getGrade, getDoctorShifts, getShifts, getDoctorsSplitShifts, getSplitShifts } from './../api/EmpMasterApi';

const JobDetails = forwardRef((props, ref) => {
     // Add validation support
     const emp_type = props.emp_type;
     const split_shift = props.split_shift;
     const [validationErrors, setValidationErrors] = useState({});
     const [deptCategories, setDeptCategories] = useState([]);
     const [mainDepartments, setMainDepartments] = useState([]);
     const [subDepartments, setSubDepartments] = useState([]);
     const [designations, setDesignations] = useState([]);
     const [positions, setPositions] = useState([]);
     const [grades, setGrades] = useState([]);
     const [doctorShifts, setDoctorShifts] = useState([]);
     const [doctorSplitShifts, setDoctorSplitShifts] = useState([]);
     const [shifts, setShifts] = useState([]);
     const [splitShifts, setSplitShifts] = useState([]);

     const [dropdownsLoading, setDropdownsLoading] = useState({
          deptCategories: false,
          mainDepartments: false,
          subDepartments: false,
          designations: false,
          positions: false,
          grades: false,
          doctorShifts: false,
          doctorSplitShifts: false,
          shifts: false,
          splitShifts: false,
     });

     // Fetch department categories, main departments, and sub departments on component mount
     useEffect(() => {
          const fetchDropdownData = async () => {
               setDropdownsLoading({
                    deptCategories: true,
                    mainDepartments: true,
                    subDepartments: true,
                    designations: true,
                    positions: true,
                    grades: true,
                    doctorShifts: true,
                    doctorSplitShifts: true,
                    shifts: true,
                    splitShifts: true,
               });

               try {
                    const [
                         deptCategoryResponse,
                         mainDeptResponse,
                         subDeptResponse,
                         designationResponse,
                         positionResponse,
                         gradeResponse,
                         doctorShiftResponse,
                         doctorSplitShiftResponse,
                         shiftResponse,
                         splitShiftResponse
                    ] = await Promise.allSettled([
                         getDeptCategory(),
                         getMainDept(),
                         getSubDept(),
                         getUserDesign(),
                         getUserPosit(),
                         getGrade(),
                         getDoctorShifts(),
                         getDoctorsSplitShifts(),
                         getShifts(),
                         getSplitShifts()
                    ]);

                    if (deptCategoryResponse.status === 'fulfilled' && deptCategoryResponse.value.status && deptCategoryResponse.value.data) {
                         setDeptCategories(deptCategoryResponse.value.data);
                    }
                    if (mainDeptResponse.status === 'fulfilled' && mainDeptResponse.value.status && mainDeptResponse.value.data) {
                         setMainDepartments(mainDeptResponse.value.data);
                    }
                    if (subDeptResponse.status === 'fulfilled' && subDeptResponse.value.status && subDeptResponse.value.data) {
                         setSubDepartments(subDeptResponse.value.data);
                    }
                    if (designationResponse.status === 'fulfilled' && designationResponse.value.status && designationResponse.value.data) {
                         setDesignations(designationResponse.value.data);
                    }
                    if (positionResponse.status === 'fulfilled' && positionResponse.value.status && positionResponse.value.data) {
                         setPositions(positionResponse.value.data);
                    }
                    if (gradeResponse.status === 'fulfilled' && gradeResponse.value.status && gradeResponse.value.data) {
                         setGrades(gradeResponse.value.data);
                    }
                    if (doctorShiftResponse.status === 'fulfilled' && doctorShiftResponse.value.status && doctorShiftResponse.value.data) {
                         setDoctorShifts(doctorShiftResponse.value.data);
                    }
                    if (doctorSplitShiftResponse.status === 'fulfilled' && doctorSplitShiftResponse.value.status && doctorSplitShiftResponse.value.data) {
                         setDoctorSplitShifts(doctorSplitShiftResponse.value.data);
                    }
                    if (shiftResponse.status === 'fulfilled' && shiftResponse.value.status && shiftResponse.value.data) {
                         setShifts(shiftResponse.value.data);
                    }
                    if (splitShiftResponse.status === 'fulfilled' && splitShiftResponse.value.status && splitShiftResponse.value.data) {
                         setSplitShifts(splitShiftResponse.value.data);
                    }
               } catch (error) {
                    console.error('Error fetching dropdown data:', error);
               } finally {
                    setDropdownsLoading({
                         deptCategories: false,
                         mainDepartments: false,
                         subDepartments: false,
                         designations: false,
                         positions: false,
                         grades: false,
                         doctorShifts: false,
                         doctorSplitShifts: false,
                         shifts: false,
                         splitShifts: false,
                    });
               }
          };
          fetchDropdownData();
     }, []);

     // Helper function to clear validation error for a specific field
     const clearFieldError = (fieldName) => {
          setValidationErrors(prev => {
               const newErrors = { ...prev };
               delete newErrors[fieldName];
               return newErrors;
          });
     };

     useImperativeHandle(ref, () => ({
          validateForm: () => {
               const errors = {};

               // Define which fields are required for JobDetails
               // if (!formData.department_category?.trim()) {
               //      errors.department_category = 'Department category is required';
               // }
               // if (!formData.main_department?.trim()) {
               //      errors.main_department = 'Main department is required';
               // }
               // if (!formData.designation_name?.trim()) {
               //      errors.designation_name = 'Designation name is required';
               // }
               // if (!formData.position?.trim()) {
               //      errors.position = 'Position is required';
               // }
               if (!formData.reporting_manager_name?.trim()) {
                    errors.reporting_manager_name = 'Reporting manager is required';
               }
               if (!formData.reporting_manager_empcode?.trim()) {
                    errors.reporting_manager_empcode = 'Reporting manager employee code is required';
               }
               // if (!formData.functional_manager_name?.trim()) {
               //      errors.functional_manager_name = 'Functional manager is required';
               // }
               // if (!formData.skip_level_manager_empcode?.trim()) {
               //      errors.skip_level_manager_empcode = 'Skip level manager employee code is required';
               // }
               if (!formData.week_off?.trim()) {
                    errors.week_off = 'Week off Type is required';
               }

               setValidationErrors(errors);
               return Object.keys(errors).length === 0; // Return true if no errors
          },
          clearValidation: () => {
               setValidationErrors({});
          }
     }));

     const [localFormData, localSetFormData] = useState({
          department_category: '',
          main_department: '',
          sub_department: '',
          designation_name: '',
          grade_name: '',
          position: '',
          reporting_manager_name: '',
          reporting_manager_empcode: '',
          functional_manager_name: '',
          skip_level_manager_empcode: '',
          shift_description: '',
          shift_description_1: '',
          shift_description_2: '',
          week_off: '',
          isLeaveApplicable: '',

     });
     const formData = props.formData !== undefined ? props.formData : localFormData;
     const setFormData = props.setFormData !== undefined ? props.setFormData : localSetFormData;


     return (
          <div className="custom_form_wrapper">
               <div className="form-grid">
                    <div className="form-floating">
                         <select
                              className={`form-select ${validationErrors.department_category ? 'is-invalid' : ''}`}
                              name="department_category"
                              id="department_category"
                              value={formData.department_category || ""}
                              onChange={e => {
                                   setFormData({ ...formData, department_category: e.target.value });
                                   if (validationErrors.department_category) clearFieldError('department_category');
                              }}
                              disabled={dropdownsLoading.deptCategories && !formData.department_category}
                         >
                              <option value="" disabled hidden>
                                   {dropdownsLoading.deptCategories && !formData.department_category
                                        ? 'Loading...'
                                        : 'Department Category'}
                              </option>
                              {formData.department_category && (
                                   <option key="current" value={formData.department_category}>
                                        {formData.department_category}
                                   </option>
                              )}
                              {!dropdownsLoading.deptCategories && (!Array.isArray(deptCategories) || deptCategories.length === 0) && !formData.department_category && (
                                   <option value="" disabled>Failed to load options</option>
                              )}
                              {!dropdownsLoading.deptCategories && Array.isArray(deptCategories) &&
                                   deptCategories
                                        .filter(category => category.dept_category !== formData.department_category)
                                        .map(category => (
                                             <option key={category.id} value={category.dept_category}>
                                                  {category.dept_category}
                                             </option>
                                        ))
                              }
                         </select>
                         <label htmlFor="department_category" style={{ opacity: formData.department_category ? 1 : 0 }}>Department Category</label>
                         {validationErrors.department_category && (
                              <div className="invalid-feedback">{validationErrors.department_category}</div>
                         )}
                    </div>
                    <div className="form-floating">
                         <select
                              className={`form-select ${validationErrors.main_department ? 'is-invalid' : ''}`}
                              name="main_department"
                              id="main_department"
                              value={formData.main_department || ""}
                              onChange={e => {
                                   setFormData({ ...formData, main_department: e.target.value });
                                   if (validationErrors.main_department) clearFieldError('main_department');
                              }}
                         >
                              <option value="" disabled hidden>Select Main Department</option>
                              {mainDepartments.map((department) => (
                                   <option key={department.id} value={department.main_dept}>
                                        {department.main_dept}
                                   </option>
                              ))}
                         </select>
                         <label htmlFor="main_department" style={{ opacity: formData.main_department ? 1 : 0 }}>Main Department</label>
                         {validationErrors.main_department && (
                              <div className="invalid-feedback">{validationErrors.main_department}</div>
                         )}
                    </div>
                    <div className="form-floating">
                         <select
                              className="form-select"
                              name="sub_department"
                              id="sub_department"
                              value={formData.sub_department || ""}
                              onChange={e => setFormData({ ...formData, sub_department: e.target.value })}
                              disabled={dropdownsLoading.subDepartments && !formData.sub_department}
                         >
                              <option value="" disabled hidden>
                                   {dropdownsLoading.subDepartments && !formData.sub_department ? 'Loading...' : 'Select Sub Department'}
                              </option>
                              {formData.sub_department && (
                                   <option key="current" value={formData.sub_department}>
                                        {formData.sub_department}
                                   </option>
                              )}
                              {!dropdownsLoading.subDepartments && (!Array.isArray(subDepartments) || subDepartments.length === 0) && !formData.sub_department && (
                                   <option value="" disabled>Failed to load options</option>
                              )}
                              {!dropdownsLoading.subDepartments && Array.isArray(subDepartments) &&
                                   subDepartments
                                        .filter(dept => dept.sub_dept !== formData.sub_department)
                                        .map(dept => (
                                             <option key={dept.id} value={dept.sub_dept}>
                                                  {dept.sub_dept}
                                             </option>
                                        ))
                              }
                         </select>
                         <label htmlFor="sub_department" style={{ opacity: formData.sub_department ? 1 : 0 }}>Sub Department</label>
                    </div>
                    <div className="form-floating">
                         <select
                              className={`form-select ${validationErrors.designation_name ? 'is-invalid' : ''}`}
                              name="designation_name"
                              id="designation_name"
                              value={formData.designation_name || ""}
                              onChange={e => {
                                   setFormData({ ...formData, designation_name: e.target.value });
                                   if (validationErrors.designation_name) clearFieldError('designation_name');
                              }}
                              disabled={dropdownsLoading.designations && !formData.designation_name}
                         >
                              <option value="" disabled hidden>
                                   {dropdownsLoading.designations && !formData.designation_name ? 'Loading...' : 'Select Designation'}
                              </option>
                              {formData.designation_name && (
                                   <option key="current" value={formData.designation_name}>
                                        {formData.designation_name}
                                   </option>
                              )}
                              {!dropdownsLoading.designations && (!Array.isArray(designations) || designations.length === 0) && !formData.designation_name && (
                                   <option value="" disabled>Failed to load options</option>
                              )}
                              {!dropdownsLoading.designations && Array.isArray(designations) &&
                                   designations
                                        .filter(designation => designation.designation_type !== formData.designation_name)
                                        .map(designation => (
                                             <option key={designation.id} value={designation.designation_type}>
                                                  {designation.designation_type}
                                             </option>
                                        ))
                              }
                         </select>
                         <label htmlFor="designation_name" style={{ opacity: formData.designation_name ? 1 : 0 }}>Designation Name</label>
                         {validationErrors.designation_name && (
                              <div className="invalid-feedback">{validationErrors.designation_name}</div>
                         )}
                    </div>
                    <div className="form-floating">
                         <select
                              className="form-select"
                              name="grade_name"
                              id="grade_name"
                              value={formData.grade_name || ""}
                              onChange={e => setFormData({ ...formData, grade_name: e.target.value })}
                              disabled={dropdownsLoading.grades && !formData.grade_name}
                         >
                              <option value="" disabled hidden>
                                   {dropdownsLoading.grades && !formData.grade_name ? 'Loading...' : 'Select Grade'}
                              </option>
                              {formData.grade_name && (
                                   <option key="current" value={formData.grade_name}>
                                        {formData.grade_name}
                                   </option>
                              )}
                              {!dropdownsLoading.grades && (!Array.isArray(grades) || grades.length === 0) && !formData.grade_name && (
                                   <option value="" disabled>Failed to load options</option>
                              )}
                              {!dropdownsLoading.grades && Array.isArray(grades) &&
                                   grades
                                        .filter(grade => grade.grade !== formData.grade_name)
                                        .map(grade => (
                                             <option key={grade.id} value={grade.grade}>
                                                  {grade.grade}
                                             </option>
                                        ))
                              }
                         </select>
                         <label htmlFor="grade_name" style={{ opacity: formData.grade_name ? 1 : 0 }}>Grade Name</label>
                    </div>
                    <div className="form-floating">
                         <select
                              className={`form-select ${validationErrors.position ? 'is-invalid' : ''}`}
                              name="position"
                              id="position"
                              value={formData.position || ""}
                              onChange={e => {
                                   setFormData({ ...formData, position: e.target.value });
                                   if (validationErrors.position) clearFieldError('position');
                              }}
                              disabled={dropdownsLoading.positions && !formData.position}
                         >
                              <option value="" disabled hidden>
                                   {dropdownsLoading.positions && !formData.position ? 'Loading...' : 'Select Position'}
                              </option>
                              {formData.position && (
                                   <option key="current" value={formData.position}>
                                        {formData.position}
                                   </option>
                              )}
                              {!dropdownsLoading.positions && (!Array.isArray(positions) || positions.length === 0) && !formData.position && (
                                   <option value="" disabled>Failed to load options</option>
                              )}
                              {!dropdownsLoading.positions && Array.isArray(positions) &&
                                   positions
                                        .filter(position => position.position_type !== formData.position)
                                        .map(position => (
                                             <option key={position.id} value={position.position_type}>
                                                  {position.position_type}
                                             </option>
                                        ))
                              }
                         </select>
                         <label htmlFor="position" style={{ opacity: formData.position ? 1 : 0 }}>Position</label>
                         {validationErrors.position && (
                              <div className="invalid-feedback">{validationErrors.position}</div>
                         )}
                    </div>
                    <div className="form-floating">
                         <input
                              type="text"
                              className={`form-control form-control-sm ${validationErrors.reporting_manager_name ? 'is-invalid' : ''}`}
                              name="reporting_manager_name"
                              id="reporting_manager_name"
                              placeholder=" "
                              value={formData.reporting_manager_name}
                              onChange={e => setFormData({ ...formData, reporting_manager_name: e.target.value })}
                              autoComplete="off"
                         />
                         <label htmlFor="reporting_manager_name">Reporting Manager Name</label>
                         {validationErrors.reporting_manager_name && (
                              <div className="invalid-feedback">{validationErrors.reporting_manager_name}</div>
                         )}
                    </div>

                    <div className="form-floating">
                         <input
                              type="text"
                              className="form-control form-control-sm"
                              name="reporting_manager_empcode"
                              id="reporting_manager_empcode"
                              placeholder=" "
                              value={formData.reporting_manager_empcode}
                              onChange={e => setFormData({ ...formData, reporting_manager_empcode: e.target.value })}
                              autoComplete="off"
                         />
                         <label htmlFor="reporting_manager_empcode">Reporting Manager Empcode</label>
                    </div>

                    <div className="form-floating">
                         <input
                              type="text"
                              className="form-control form-control-sm"
                              name="functional_manager_name"
                              id="functional_manager_name"
                              placeholder=" "
                              value={formData.functional_manager_name}
                              onChange={e => setFormData({ ...formData, functional_manager_name: e.target.value })}
                              autoComplete="off"
                         />
                         <label htmlFor="functional_manager_name">Functional Manager Name</label>
                    </div>

                    <div className="form-floating">
                         <input
                              type="text"
                              className="form-control form-control-sm"
                              name="skip_level_manager_empcode"
                              id="skip_level_manager_empcode"
                              placeholder=" "
                              value={formData.skip_level_manager_empcode}
                              onChange={e => setFormData({ ...formData, skip_level_manager_empcode: e.target.value })}
                              autoComplete="off"
                         />
                         <label htmlFor="skip_level_manager_empcode">Skip Level Manager Empcode</label>
                    </div>

                    <div className="form-floating">
                         {emp_type === "DOCTOR" ? (
                              split_shift === 'Y' ? (
                                   // If split_shift is 'Y'
                                   <>
                                        <table style={{ width: "100%" }}>
                                             <tr>
                                                  <td><small>Doctors Split Shift 1</small></td>
                                                  <td><small>Doctors Split Shift 2</small></td>
                                             </tr>
                                             <tr>
                                                  <td>
                                                       <select
                                                            className="form-select"
                                                            name="shift_description_1"
                                                            id="shift_description_1"
                                                            value={formData.shift_description_1 || ""}
                                                            onChange={e => setFormData({ ...formData, shift_description_1: e.target.value })}
                                                            autoComplete="off"
                                                            disabled={dropdownsLoading.doctorSplitShifts && !formData.shift_description_1}
                                                       >
                                                            <option value="" disabled hidden>
                                                                 {dropdownsLoading.doctorSplitShifts && !formData.shift_description_1 ? 'Loading...' : 'Select Doctor Split Shift'}
                                                            </option>
                                                            {formData.shift_description_1 && (
                                                                 <option key="current" value={formData.shift_description_1}>
                                                                      {formData.shift_description_1}
                                                                 </option>
                                                            )}
                                                            {!dropdownsLoading.doctorSplitShifts && (!Array.isArray(doctorSplitShifts) || doctorSplitShifts.length === 0) && !formData.shift_description_1 && (
                                                                 <option value="" disabled>Failed to load options</option>
                                                            )}
                                                            {!dropdownsLoading.doctorSplitShifts && Array.isArray(doctorSplitShifts) &&
                                                                 doctorSplitShifts
                                                                      .filter(shift => shift.shift_name !== formData.shift_description_1)
                                                                      .map(shift => (
                                                                           <option key={shift.id} value={shift.shift_name}>
                                                                                {shift.shift_name} ({shift.in_time} - {shift.out_time})
                                                                           </option>
                                                                      ))
                                                            }
                                                       </select>
                                                  </td>
                                                  <td>
                                                       <select
                                                            className="form-select"
                                                            name="shift_description_2"
                                                            id="shift_description_2"
                                                            value={formData.shift_description_2 || ""}
                                                            onChange={e => setFormData({ ...formData, shift_description_2: e.target.value })}
                                                            autoComplete="off"
                                                            disabled={dropdownsLoading.doctorSplitShifts && !formData.shift_description_2}
                                                       >
                                                            <option value="" disabled hidden>
                                                                 {dropdownsLoading.doctorSplitShifts && !formData.shift_description_2 ? 'Loading...' : 'Select Doctor Split Shift'}
                                                            </option>
                                                            {formData.shift_description_2 && (
                                                                 <option key="current" value={formData.shift_description_2}>
                                                                      {formData.shift_description_2}
                                                                 </option>
                                                            )}
                                                            {!dropdownsLoading.doctorSplitShifts && (!Array.isArray(doctorSplitShifts) || doctorSplitShifts.length === 0) && !formData.shift_description_2 && (
                                                                 <option value="" disabled>Failed to load options</option>
                                                            )}
                                                            {!dropdownsLoading.doctorSplitShifts && Array.isArray(doctorSplitShifts) &&
                                                                 doctorSplitShifts
                                                                      .filter(shift => shift.shift_name !== formData.shift_description_2)
                                                                      .map(shift => (
                                                                           <option key={shift.id} value={shift.shift_name}>
                                                                                {shift.shift_name} ({shift.in_time} - {shift.out_time})
                                                                           </option>
                                                                      ))
                                                            }
                                                       </select>
                                                  </td>
                                             </tr>
                                        </table>
                                   </>
                              ) : (
                                   // Otherwise, render doctor shift select
                                   <>
                                        <select
                                             className="form-select"
                                             name="shift_description"
                                             id="shift_description"
                                             value={formData.shift_description || ""}
                                             onChange={e => setFormData({ ...formData, shift_description: e.target.value })}
                                             disabled={dropdownsLoading.doctorShifts && !formData.shift_description}
                                        >
                                             <option value="" disabled hidden>
                                                  {dropdownsLoading.doctorShifts && !formData.shift_description ? 'Loading...' : 'Select Doctor Shift'}
                                             </option>
                                             {formData.shift_description && (
                                                  <option key="current" value={formData.shift_description}>
                                                       {formData.shift_description}
                                                  </option>
                                             )}
                                             {!dropdownsLoading.doctorShifts && (!Array.isArray(doctorShifts) || doctorShifts.length === 0) && !formData.shift_description && (
                                                  <option value="" disabled>Failed to load options</option>
                                             )}
                                             {!dropdownsLoading.doctorShifts && Array.isArray(doctorShifts) &&
                                                  doctorShifts
                                                       .filter(shift => shift.shift_name !== formData.shift_description)
                                                       .map(shift => (
                                                            <option key={shift.id} value={shift.shift_name}>
                                                                 {shift.shift_name} ({shift.in_time} - {shift.out_time})
                                                            </option>
                                                       ))
                                             }
                                        </select>
                                        <label htmlFor="shift_description">{emp_type} Default Shift </label>
                                   </>
                              )
                         ) : (
                              // For non-doctor, render regular shift select
                              split_shift === 'Y' ? (
                                   <>
                                        <table style={{ width: "100%" }}>
                                             <tr>
                                                  <td>Split Shift 1</td>
                                                  <td>Split Shift 2</td>
                                             </tr>
                                             <tr>
                                                  <td>
                                                       <select
                                                            className="form-select"
                                                            name="shift_description_1"
                                                            id="shift_description_1"
                                                            value={formData.shift_description_1 || ""}
                                                            onChange={e => setFormData({ ...formData, shift_description_1: e.target.value })}
                                                            disabled={dropdownsLoading.splitShifts && !formData.shift_description_1}
                                                       >
                                                            <option value="" disabled hidden>
                                                                 {dropdownsLoading.splitShifts && !formData.shift_description_1 ? 'Loading...' : 'Select Shift'}
                                                            </option>
                                                            {formData.shift_description_1 && (
                                                                 <option key="current" value={formData.shift_description_1}>
                                                                      {formData.shift_description_1}
                                                                 </option>
                                                            )}
                                                            {!dropdownsLoading.splitShifts && (!Array.isArray(splitShifts) || splitShifts.length === 0) && !formData.shift_description_1 && (
                                                                 <option value="" disabled>Failed to load options</option>
                                                            )}
                                                            {!dropdownsLoading.splitShifts && Array.isArray(splitShifts) &&
                                                                 splitShifts
                                                                      .filter(shift => shift.ShiftName !== formData.shift_description_1)
                                                                      .map(shift => (
                                                                           <option key={shift.id} value={shift.ShiftName}>
                                                                                {shift.ShiftName} {shift.ShiftStart} - {shift.ShiftEnd}
                                                                           </option>
                                                                      ))
                                                            }
                                                       </select>



                                                  </td>
                                                  <td>
                                                       <select
                                                            className="form-select"
                                                            name="shift_description_2"
                                                            id="shift_description_2"
                                                            value={formData.shift_description_2 || ""}
                                                            onChange={e => setFormData({ ...formData, shift_description_2: e.target.value })}
                                                            disabled={dropdownsLoading.splitShifts && !formData.shift_description_2}
                                                       >
                                                            <option value="" disabled hidden>
                                                                 {dropdownsLoading.splitShifts && !formData.shift_description_2 ? 'Loading...' : 'Select Shift'}
                                                            </option>
                                                            {formData.shift_description_2 && (
                                                                 <option key="current" value={formData.shift_description_2}>
                                                                      {formData.shift_description_2}
                                                                 </option>
                                                            )}
                                                            {!dropdownsLoading.splitShifts && (!Array.isArray(splitShifts) || splitShifts.length === 0) && !formData.shift_description_2 && (
                                                                 <option value="" disabled>Failed to load options</option>
                                                            )}
                                                            {!dropdownsLoading.splitShifts && Array.isArray(splitShifts) &&
                                                                 splitShifts
                                                                      .filter(shift => shift.ShiftName !== formData.shift_description_2)
                                                                      .map(shift => (
                                                                           <option key={shift.id} value={shift.ShiftName}>
                                                                                {shift.ShiftName} {shift.ShiftStart} - {shift.ShiftEnd}
                                                                           </option>
                                                                      ))
                                                            }
                                                       </select>

                                                  </td>
                                             </tr>

                                        </table>

                                   </>) : (
                                   <>
                                        <select
                                             className="form-select"
                                             name="shift_description"
                                             id="shift_description"
                                             value={formData.shift_description || ""}
                                             onChange={e => setFormData({ ...formData, shift_description: e.target.value })}
                                        >
                                             <option value="" disabled hidden>Select Shift</option>
                                             {shifts.map((shift) => (
                                                  <option key={shift.id} value={shift.ShiftName}>
                                                       {shift.ShiftName} {shift.ShiftStart} - {shift.ShiftEnd}
                                                  </option>
                                             ))}
                                        </select>
                                        <label htmlFor="shift_description">{emp_type} Default Shift </label>
                                   </>
                              )
                         )}
                    </div>
                    {/* New field needed here , select work type, having options Case Based, Target, MG, Regular */}
                    {emp_type === "DOCTOR" ? (
                         <div className="form-floating">
                              <select
                                   className="form-select"
                                   name="work_type"
                                   id="work_type"
                                   value={formData.work_type || ""}
                                   onChange={e => setFormData({ ...formData, work_type: e.target.value })}
                              >
                                   <option value="" disabled hidden>Select Work Type</option>
                                   <option value="Case Based">Case Based</option>
                                   <option value="Target">Target</option>
                                   <option value="MG">MG</option>
                                   <option value="Regular">Regular</option>
                              </select>
                              <label htmlFor="work_type" style={{ opacity: formData.work_type ? 1 : 0 }}>Work Type</label>
                         </div>
                    ) : (
                         // For non-doctors, default to Regular and hide the field
                         <input type="hidden" name="work_type" value="Regular" />
                    )}


                    <div className="form-floating">
                         <select
                              className="form-select"
                              name="week_off"
                              id="week_off"
                              value={formData.week_off || ""}
                              onChange={e => setFormData({ ...formData, week_off: e.target.value })}
                              required
                         >
                              <option value="" disabled hidden>Week End Type</option>
                              <option value="Fixed_weekend">Fixed_weekend</option>
                              <option value="Custom_weekend">Custom_weekend</option>
                         </select>
                         <label htmlFor="week_off" style={{ opacity: formData.week_off ? 1 : 0 }}>Week Off</label>

                    </div>

                    <div className="form-floating">
                         <select
                              className="form-select"
                              name="isLeaveApplicable"
                              id="isLeaveApplicable"
                              value={formData.isLeaveApplicable || " "}
                              onChange={e => setFormData({ ...formData, isLeaveApplicable: e.target.value })}
                         >
                              <option value=" " disabled hidden>Leave Applicable</option>
                              <option value="Y">Yes</option>
                              <option value="N">No</option>
                         </select>
                         <label htmlFor="isLeaveApplicable" style={{ opacity: formData.isLeaveApplicable ? 1 : 0 }}>Leave Applicable</label>
                    </div>

               </div>
          </div>
     );
});

export default JobDetails;